// PlayerOverlay: one overlay shell, interchangeable content.
//
// Every state the player needs to surface (empty, paused, buffering, error,
// autoplay-blocked) renders through this component so they share layout,
// scrim, and typography. Only the text and the optional action differ.
//
// `variant` drives styling only; the caller owns the copy. Keep it that way —
// putting the strings in here would make the messages hard to vary per state.

import type { ReactNode } from "react";

export type OverlayVariant =
  | "empty"
  | "paused"
  | "buffering"
  | "error"
  | "autoplay";

export interface PlayerOverlayProps {
  variant: OverlayVariant;
  /** Primary line. Keep it short — it renders large and centered. */
  title: string;
  /** Optional secondary line for context. */
  detail?: string;
  /** Optional icon/glyph rendered above the title. */
  icon?: ReactNode;
  /** Optional call to action (e.g. the autoplay unlock button). */
  action?: ReactNode;
}

export function PlayerOverlay(props: PlayerOverlayProps): JSX.Element {
  return (
    <div
      className={`overlay overlay-${props.variant}`}
      role="status"
      aria-live="polite"
    >
      <div className="overlay-content">
        {props.icon !== undefined && (
          <div className="overlay-icon" aria-hidden="true">
            {props.icon}
          </div>
        )}
        <p className="overlay-title">{props.title}</p>
        {props.detail !== undefined && (
          <p className="overlay-detail">{props.detail}</p>
        )}
        {props.action !== undefined && (
          <div className="overlay-action">{props.action}</div>
        )}
      </div>
    </div>
  );
}
